import { Flag, FlagFilled } from '@carbon/icons-react';
import { Pad, PadBox } from 'component/basics';
import { CTAButton, SubtleButton } from 'component/button';
import { colorSurfacePrimary } from 'component/color';
import type { CommentConfig } from 'component/comment';
import { commentTable } from 'component/comment';
import { Checkbox } from 'component/form';
import { Popup } from 'component/popup';
import { UtilityText } from 'component/text';
import { GUIDELINES } from 'feature/question/community-guidelines';
import { useState } from 'react';
import { View } from 'react-native-web';
import { useHasCapability } from 'util/admin';
import { useDatastore, useObject, usePersonaKey, useSessionData } from 'util/datastore';
import type { Feature, TLabel } from 'util/stdtypes';
import { labelToKey, t } from 'util/stdtypes';

import { moderatedCommentTable } from './ai-premoderator';

export const ReportCommentFeature: Feature<CommentConfig, {}> = {
    name: 'Report Comment',
    key: 'report_comment',
    dev: true,
    providesConfig: {
        commentRightActions: [ActionReportComment],
    },
};

export function ActionReportComment({ commentKey }) {
    const reported = useSessionData(['reportedComments', commentKey]);
    const comment = useObject(commentTable, commentKey);
    const personaKey = usePersonaKey();

    if (!personaKey) return null;
    if (comment?.from == personaKey) return null;

    const reportPopup = ({ onClose }) => <ReportPopup commentKey={commentKey} onClose={onClose} />;
    const alreadyPopup = () => <UtilityText label={t('You have already reported this')} />;

    return (
        <Popup popupContent={reported ? alreadyPopup : reportPopup} testID='report'>
            <SubtleButton ariaLabel={reported ? t('Reported') : t('Report')} icon={reported ? FlagFilled : Flag} onPress={true} />
        </Popup>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ReportPopup({ onClose, commentKey }) {
    const [checkedRules, setcheckedRules] = useState<string[]>([]);
    const comment = useObject(moderatedCommentTable, commentKey);
    const datastore = useDatastore();
    const canRemove = useHasCapability('remove-comment');

    const guideline_names: TLabel[] = [...Object.values(GUIDELINES), t('Other')];

    if (!comment) return null;

    function onToggle(name) {
        if (checkedRules.includes(name)) {
            setcheckedRules(checkedRules.filter((rule) => rule !== name));
        } else {
            setcheckedRules([...checkedRules, name]);
        }
    }

    async function onReport() {
        if (!comment) return;
        datastore.setSessionData(['reportedComments', commentKey], true);
        onClose();
        await datastore.callServerAsync('moderation', 'sendToReview', {
            type: 'comment',
            key: commentKey,
            source: 'report',
            text: comment.text,
            violations: JSON.stringify(checkedRules),
        });
    }

    async function onRemove() {
        if (!comment) return;
        onClose();
        await datastore.callServerAsync('moderation', 'adminSetCommentViolates', {
            commentKey,
            appealText: '',
            violates: !comment.violates,
        });
    }

    return (
        <View style={{ maxWidth: 300, backgroundColor: colorSurfacePrimary }}>
            <UtilityText label={t('What guideline does this violate? Select all that apply.')} type='small' weight='medium' />
            <Pad size={12} />
            {guideline_names.map((name) => (
                <Checkbox
                    key={labelToKey(name)}
                    label={name}
                    onChange={() => onToggle(name)}
                    value={checkedRules.includes(labelToKey(name))}
                />
            ))}
            <Pad size={12} />
            <CTAButton disabled={checkedRules.length === 0} label={t('Report')} onPress={onReport} testID='report-submit' wide />
            {canRemove && (
                <PadBox top={12}>
                    <CTAButton
                        label={comment.violates ? t('Restore') : t('Remove')}
                        onPress={onRemove}
                        testID='remove'
                        type='accent'
                        wide
                    />
                </PadBox>
            )}
        </View>
    );
}
